import { useState } from "react";
import { Link, useRouter } from "@tanstack/react-router";
import {
  buttonVariants,
  cn,
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuContent,
} from "web-design-system";
import {
  FileTextIcon,
  PieChartIcon,
  UserIcon,
  UsersIcon,
  Sparkles,
  LogOut,
  SunIcon,
  MoonIcon,
  BarChartIcon,
  BriefcaseBusinessIcon,
  HomeIcon,
} from "lucide-react";
import { useAuthContext } from "@/providers/auth-provider";
import {
  useDistributorBranding,
  useDistributorProfile,
  useLogout,
} from "creso-core/hooks";
import { handleError } from "@/utils/error";
import { User } from "creso-core/types";
import { useTheme } from "@/theme";
import { Notifications } from "@/modules/notifications/components";
import { Calculator } from "@/modules/calculator/components/calculator";
import {
  usernameWithoutPrefix,
  isEmployeeUser,
  isEmployeeUserWithEmployeeRole,
  shouldEnableRBAC,
} from "creso-core/utils";
import AppLogo from "../logo/app-logo";
import { WhatsAppIcon } from "../icon/whatsapp-icon";
import { EmployeeAccountSettingsSection } from "./employee-account-settings-section";
import { PartnerAccountSettingsSection } from "./partner-account-settings-section";
import { AccountDetailsDialog } from "./account-details-dialog";

interface MenuItem {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  path: string;
  search?: (prev: Record<string, unknown>) => Record<string, unknown>;
  show: (user: User) => boolean;
}

interface DropdownItem {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  onClick?: () => void;
}

const menuItems: MenuItem[] = [
  {
    label: "Home",
    icon: HomeIcon,
    path: "/home",
    show: () => true,
  },
  {
    label: "Clients",
    icon: UsersIcon,
    path: "/clients",
    show: (user) => user?.user_type !== "investor",
  },
  {
    label: "Mutual Funds",
    icon: PieChartIcon,
    path: "/funds",
    show: () => true,
  },
  {
    label: "Insights",
    icon: BarChartIcon,
    path: "/insights",
    show: (user) => !isEmployeeUserWithEmployeeRole(user),
  },
  {
    label: "Reports",
    icon: FileTextIcon,
    path: "/reports",
    show: (user) => user?.user_type !== "investor",
  },
  {
    label: "Resources",
    icon: BriefcaseBusinessIcon,
    path: "/resources",
    show: () => true,
  },
];

function accountMenuRoleLabel(userType: User["user_type"]): string {
  if (userType === "employee") return "Team";
  if (userType === "investor") return "Investor";
  return "Partner";
}

function initialsFromName(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "";
  return (first + last).toUpperCase() || "?";
}

export function AppNavbar() {
  const { user, employee } = useAuthContext();
  const [accountDetailsOpen, setAccountDetailsOpen] = useState(false);
  const router = useRouter();
  const { mutateAsync: logout } = useLogout();
  const isRBACEnabled = shouldEnableRBAC(user);
  const isEmployee = isEmployeeUser(user?.user_type);
  const arnNumber = user?.username
    ? usernameWithoutPrefix(user.username)
    : null;

  const { data: profile } = useDistributorProfile();
  const { data: branding } = useDistributorBranding(true);

  const organizationLine =
    branding?.brand_name ||
    profile?.name ||
    employee?.name ||
    user?.username ||
    "";
  const roleLabel = accountMenuRoleLabel(user.user_type);
  const isTeamMemberWithEmployeeRole = isEmployeeUserWithEmployeeRole(user);
  const personName = employee?.name || profile?.name || user?.username || "";
  const loginIdDisplay = user.username?.trim() || "";
  const employeePhone = employee?.phone_number?.trim() || "—";
  const employeeEmail = employee?.email?.trim() || "—";

  const { theme, setTheme } = useTheme();

  const handleLogout = async () => {
    try {
      await logout();
      router.navigate({ to: "/login/distributor" });
    } catch (error) {
      handleError(error, "Failed to logout. Please try again.");
    }
  };

  const navigate = (path: string) => {
    router.navigate({ to: path });
  };

  const items: DropdownItem[] = [
    ...(isRBACEnabled
      ? [
          {
            label: "Team",
            icon: UsersIcon,
            onClick: () => navigate("/account/team-members"),
          },
        ]
      : []),
    ...(!isEmployee
      ? [
          {
            label: "Branding",
            icon: Sparkles,
            onClick: () => navigate("/account/branding"),
          },
        ]
      : []),
    {
      label: theme === "dark" ? "Light Mode" : "Dark Mode",
      onClick: () => setTheme(theme === "dark" ? "light" : "dark"),
      icon: theme === "dark" ? SunIcon : MoonIcon,
    },
    {
      label: "Log out",
      icon: LogOut,
      onClick: handleLogout,
    },
  ];

  return (
    <div className="flex h-dvh flex-shrink-0 flex-col items-center bg-secondary py-4">
      <Link to="/home">
        <AppLogo />
      </Link>
      <div className="flex flex-col flex-1 items-center gap-3 justify-between px-2">
        <nav className="flex flex-col gap-3 mt-3 flex-1">
          {menuItems
            .filter((item) => item.show(user))
            .map(({ label, icon: Icon, path, search }) => (
              <Link
                key={label}
                className={cn(
                  "flex rounded-md bg-secondary !no-underline flex-col w-14 gap-1.5 group text-center min-h-12 h-auto p-2 text-xs items-center text-muted-foreground",
                  "data-[status=active]:font-semibold data-[status=active]:text-foreground data-[status=active]:bg-muted",
                  "hover:bg-accent/80 transition-colors"
                )}
                to={path}
                search={search?.(
                  router.state.location.search as Record<string, unknown>
                )}
              >
                <Icon
                  className={cn(
                    "size-5 group-data-[status=active]:stroke-primary"
                  )}
                />
                <span className="text-xxs">{label}</span>
              </Link>
            ))}
        </nav>

        <a
          href={`https://wa.me/919082283878?text=Hi, I need help with an issue (ARN-${arnNumber})`}
          target="_blank"
          rel="noopener noreferrer"
          className={cn(buttonVariants({ variant: "ghost", size: "icon" }))}
        >
          <WhatsAppIcon className="!size-5" color="var(--primary)" />
        </a>

        <Calculator />

        <Notifications />

        {/* <button
          type="button"
          onClick={() => setNoteTakerOpen(true)}
          className={cn(
            buttonVariants({ variant: "ghost", size: "icon" }),
            "transition-colors"
          )}
          title="AI Note Taker"
        >
          <Mic className="!size-5" />
        </button> */}

        <>
          <DropdownMenu>
            <DropdownMenuTrigger
              className={cn(
                buttonVariants({ variant: "outline", size: "icon" }),
                "transition-all border-none size-9 bg-muted hover:bg-secondary"
              )}
            >
              <UserIcon />
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-55 flex flex-col rounded-md border border-border shadow-md p-0"
              side="right"
              align="end"
              sideOffset={10}
            >
              {isEmployee ? (
                <EmployeeAccountSettingsSection
                  personName={personName}
                  isEmployeeRole={isTeamMemberWithEmployeeRole}
                  organizationLine={organizationLine}
                  brandingLogo={branding?.brand_logo as string | undefined}
                  onOpenAccountDetails={() => setAccountDetailsOpen(true)}
                  getInitials={initialsFromName}
                />
              ) : (
                <PartnerAccountSettingsSection
                  organizationLine={organizationLine}
                  roleLabel={roleLabel}
                  brandingLogo={branding?.brand_logo as string | undefined}
                  onOpenAccountSettings={() => navigate("/account/setting")}
                />
              )}
              {items.length > 0 && (
                <div className="py-1">
                  {items.map(({ label, icon: Icon, onClick }) => (
                    <DropdownMenuItem
                      key={label}
                      onClick={onClick}
                      className="flex cursor-pointer items-center justify-between px-4 py-2"
                    >
                      <span>{label}</span>
                      <Icon className="size-4 shrink-0 text-muted-foreground" />
                    </DropdownMenuItem>
                  ))}
                </div>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          <AccountDetailsDialog
            open={accountDetailsOpen}
            onOpenChange={setAccountDetailsOpen}
            personName={personName}
            isEmployeeRole={isTeamMemberWithEmployeeRole}
            loginIdDisplay={loginIdDisplay}
            employeePhone={employeePhone}
            employeeEmail={employeeEmail}
            organizationLine={organizationLine}
            brandingLogo={branding?.brand_logo as string | undefined}
            getInitials={initialsFromName}
          />
        </>
      </div>
    </div>
  );
}
